// src/pages/BookingsPage.js

import React, { useState, useEffect } from 'react';
import { Container, Typography, List, Divider } from '@mui/material';
import { getUserTransactions } from '../services/api';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuth } from '../context/AuthContext';
import BookingCategoryItem from '../components/BookingCategoryItem';

function BookingsPage() {
    const { currentUser, loading } = useAuth();
    const [bookingCounts, setBookingCounts] = useState({
        active: 0,
        completed: 0,
        outdated: 0
    });

    // Define and call fetchBookings inline here
    useEffect(() => {
        if (!loading && currentUser) {
            const fetchBookings = async () => {
                try {
                    const response = await getUserTransactions();
                    const borrowerBookings = response.data.filter(
                        (booking) => booking.borrower_id === currentUser.id
                    );

                    const counts = {
                        active: borrowerBookings.filter((booking) =>
                            ['pending', 'accepted', 'paid', 'in_progress'].includes(booking.status)
                        ).length,
                        completed: borrowerBookings.filter(
                            (booking) => booking.status === 'completed'
                        ).length,
                        outdated: borrowerBookings.filter((booking) =>
                            ['cancelled', 'failed', 'declined'].includes(booking.status)
                        ).length
                    };

                    setBookingCounts(counts);
                } catch (error) {
                    console.error('Error fetching bookings:', error);
                }
            };

            fetchBookings();
        }
    }, [loading, currentUser]);

    const bookingCategories = [
        {
            title: 'Current Bookings',
            count: bookingCounts.active,
            description: 'Chats, requests and current bookings',
            icon: <AssignmentIcon />,
            to: '/current-bookings'
        },
        {
            title: 'Completed Bookings',
            count: bookingCounts.completed,
            description: 'All completed bookings. Remember to give a review!',
            icon: <CheckCircleIcon />,
            to: '/completed-bookings'
        },
        {
            title: 'Outdated Bookings',
            count: bookingCounts.outdated,
            description: 'Cancelled or expired bookings',
            icon: <CancelIcon />,
            to: '/outdated-bookings'
        }
    ];

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Bookings
                </Typography>
                <Typography variant="body1" paragraph>
                    Loading your bookings...
                </Typography>
            </Container>
        );
    }

    if (!currentUser) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Bookings
                </Typography>
                <Typography variant="body1" paragraph>
                    You need to be logged in to view your bookings.
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom>
                What I'm borrowing...
            </Typography>
            <Typography variant="body1" paragraph>
                Your bookings are listed here. Keep in mind that it is nice to write
                something if you cancel a request. If you have any questions - please
                contact us on the support team!
            </Typography>

            <List>
                {bookingCategories.map((category, index) => (
                    <React.Fragment key={category.title}>
                        <BookingCategoryItem
                            title={category.title}
                            count={category.count}
                            description={category.description}
                            icon={category.icon}
                            to={category.to}
                        />
                        {index < bookingCategories.length - 1 && (
                            <Divider variant="inset" component="li" />
                        )}
                    </React.Fragment>
                ))}
            </List>
        </Container>
    );
}

export default BookingsPage;
